<script setup>
import {
  submitNewPaymentMethod,
  updateCard,
} from '#common/services/paymentServices'
import { scrollToError } from '#common/utils/form'

const customTheme = {
  showPlaceholders: false,
  showDobLabels: false,
  cardholderName: {
    classes: 'medium-12 end',
  },
  billingSameAsShippingCheckbox: {
    classes: 'small-12 checkmark-form-input',
  },
  savePaymentDetailsCheckbox: {
    classes: 'small-12 checkmark-form-input',
  },
  makeDefaultCheckbox: {
    classes: 'small-12 checkmark-form-input',
  },
  billingAddress: {
    line1: {
      classes: 'medium-12 end',
    },
    line2: {
      classes: 'medium-12 end',
    },
    city: {
      classes: 'medium-6',
    },
    state: {
      classes: 'medium-6',
    },
    zip: {
      classes: 'medium-8 end',
    },
  },
}

const emit = defineEmits([
  'newOneTimeCcPayment',
  'newCcPaymentSaved',
  'ccPaymentSaved',
  'cancelClicked',
])
const props = defineProps({
  formName: {
    type: String,
    required: false,
    default: 'checkout-credit-card-form',
  },
  shippingAddress: {
    type: Object,
    required: false,
    default: null,
  },
  showMakeDefaultCheckbox: {
    type: Boolean,
    required: false,
    default: true,
  },
  showSavePaymentDetailsCheckbox: {
    type: Boolean,
    default: true,
  },
  forceDefault: {
    type: Boolean,
    required: false,
    default: false,
  },
  isNewCreditCard: {
    type: Boolean,
    required: false,
    default: true,
  },
  includeNumber: {
    type: Boolean,
    required: false,
    default: true,
  },
  hideButtons: {
    type: Boolean,
    required: false,
    default: true,
  },
  initial: {
    type: Object,
    default() {
      return {}
    },
  },
  submitButtonClass: {
    type: String,
    default: 'button button-solid',
  },
  submitButtonText: {
    type: String,
    default: 'Save',
  },
  hostedFieldsStyles: {
    type: Object,
    required: false,
    default() {
      return null
    },
  },
  addressLineOneId: {
    type: String,
    default: 'billing1',
  },
  useCheckoutButtons: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  floating: {
    type: Boolean,
    required: false,
    default: false,
  },
  idPrefix: {
    type: String,
    default: '',
  },
})
const { $axios } = useNuxtApp()
const submitErrors = ref('')
const ccForm = ref(null)
const saving = ref(false)
const savePaymentDetails = ref(true)

async function submitCCForm() {
  saving.value = true
  const tokenizedData = await ccForm.value.validateAndGetToken()

  if (tokenizedData === null) {
    scrollToError(nextTick)
    saving.value = false
    return false
  }

  // We don't save the payment to server if this is false
  if (!savePaymentDetails.value) {
    emit('newOneTimeCcPayment', tokenizedData)
    saving.value = false
    return true
  }

  if (props.isNewCreditCard) {
    return await saveNewPaymentMethod(tokenizedData)
  } else {
    return await saveUpdatedPaymentMethod(tokenizedData)
  }
}
async function saveNewPaymentMethod(tokenizedData) {
  try {
    const result = await submitNewPaymentMethod($axios, tokenizedData)
    result.data.formName = props.formName
    emit('newCcPaymentSaved', result.data)
    return true
  } catch (e) {
    Sentry.captureException(e)
    console.error('Failed adding card', e)
    if (e.response.data) {
      submitErrors.value = e.response.data.display_error
      scrollToError(nextTick)
    }
    return false
  } finally {
    saving.value = false
  }
}
async function saveUpdatedPaymentMethod(tokenizedData) {
  try {
    const result = await updateCard(
      $axios,
      props.initial?.card_id,
      tokenizedData,
    )
    result.data.formName = props.formName
    emit('ccPaymentSaved', result.data)
    return true
  } catch (e) {
    Sentry.captureException(e)
    console.error('Failed editing card', e)
    if (e.response.data) {
      submitErrors.value = e.response.data.display_error
      scrollToError(nextTick)
    }
    return false
  } finally {
    saving.value = false
  }
}
function savePaymentDetailsClicked(value) {
  savePaymentDetails.value = value
}
function cancel() {
  emit('cancelClicked')
}

defineExpose({
  submitCCForm,
})
</script>

<template>
  <div class="row">
    <div class="columns medium-10 cc-form-container">
      <CreditCardFormFK
        ref="ccForm"
        class="standard-form form-container payment-creditcard"
        :include-number="includeNumber"
        :save-error="submitErrors"
        :hide-buttons="hideButtons"
        :custom-theme="customTheme"
        :name="formName"
        :show-pay-pal-button="false"
        :show-billing-same-as-shipping-checkbox="true"
        :show-save-payment-details-checkbox="showSavePaymentDetailsCheckbox"
        :show-make-default-checkbox="showMakeDefaultCheckbox"
        :shipping-address="shippingAddress"
        :force-default="forceDefault"
        :initial="initial"
        :hosted-fields-styles="hostedFieldsStyles"
        :submit-button-class="submitButtonClass"
        :submit-button-text="submitButtonText"
        :address-line-one-id="addressLineOneId"
        :disabled="disabled || saving"
        @savePaymentDetailsClicked="savePaymentDetailsClicked"
      >
        <template v-if="floating" #nameForm="nameSlotProps">
          <FloatingNameFormFK
            :id-prefix="idPrefix"
            :validation-rules="nameSlotProps.validationRules"
          />
        </template>
        <template v-if="floating" #hostedRowForm="hostedSlotProps">
          <FloatingHostedFieldsForm
            :tokenize-errors="hostedSlotProps.tokenizeErrors"
            :include-number="hostedSlotProps.includeNumber"
            :initial="hostedSlotProps.initial"
          />
        </template>
        <template v-if="floating" #addressForm="addressSlotProps">
          <div>
            <FloatingAddressFormFK
              :id-prefix="idPrefix"
              :state-options="addressSlotProps.stateOptions"
              @addressAutocomplete="addressSlotProps.autoCompletePlaceChanged"
            />
          </div>
        </template>
        <template #after-form>
          <slot name="after-form">
            <div class="row">
              <CheckoutSectionSave
                v-if="useCheckoutButtons"
                :save-disabled="disabled || saving"
                :cancel-disabled="disabled || saving"
                :show-cancel-button="showCancelButton"
                save-button-text="Use This Payment Method"
                @saveClicked="submitCCForm"
                @cancelClicked="cancel"
              />
            </div>
          </slot>
        </template>
      </CreditCardFormFK>
    </div>
  </div>
</template>
